/** CMS-editable Refund & Cancellation Policy for /refund-cancellation-policy. */

export const REFUND_SECTION_ICON_OPTIONS = [
  { value: "file", label: "Document" },
  { value: "ban", label: "Cancellation / Ban" },
  { value: "wallet", label: "Refund Eligibility / Wallet" },
  { value: "sync", label: "Process / Sync" },
  { value: "clock", label: "Timeline / Clock" },
  { value: "headset", label: "Need Help / Support" },
];

export const REFUND_HERO_INTRO =
  "We want you to be completely satisfied with your experience on Trocanet. Please read our refund and cancellation policy carefully.";

export const DEFAULT_REFUND_SECTIONS = [
  {
    id: "overview",
    title: "Overview",
    body: `<p>This policy applies to all transactions made on Trocanet marketplace including products, services, subscriptions and other paid services unless stated otherwise.</p>`,
    icon: "file",
  },
  {
    id: "cancellation-policy",
    title: "Cancellation Policy",
    body: `<ul>
<li>Buyers can cancel order before the seller confirms or ships the order.</li>
<li>Once the order is shipped or service has started, cancellation is not possible.</li>
<li>For customized or made-to-order items, cancellation is not allowed.</li>
</ul>`,
    icon: "ban",
  },
  {
    id: "refund-eligibility",
    title: "Refund Eligibility",
    body: `<ul>
<li>Eligible refunds will be processed to the original payment method.</li>
<li>Refunds are applicable only if the product/service is not delivered, defective or not as described.</li>
<li>Refunds are not applicable for services already rendered or consumed.</li>
</ul>`,
    icon: "wallet",
  },
  {
    id: "refund-process",
    title: "Refund Process",
    body: `<ul>
<li>Go to &quot;My Orders&quot; and select the order.</li>
<li>Click on &quot;Request Refund&quot; and provide the required details.</li>
<li>Our team will review your request and respond within 2 business days.</li>
<li>If approved, the refund will be processed as per the timelines below.</li>
</ul>`,
    icon: "sync",
  },
  {
    id: "refund-timeline",
    title: "Refund Timeline",
    body: `<div class="hub-refund-table-wrap"><table class="hub-refund-table">
<thead>
<tr>
<th>Payment Method</th>
<th>UPI / Wallet</th>
<th>Credit / Debit Card</th>
<th>Net Banking</th>
<th>Bank Transfer (NEFT/RTGS)</th>
</tr>
</thead>
<tbody>
<tr>
<td><strong>Refund Time</strong></td>
<td>1-2 Business Days</td>
<td>5-7 Business Days</td>
<td>5-7 Business Days</td>
<td>7-10 Business Days</td>
</tr>
</tbody>
</table></div>`,
    icon: "clock",
  },
  {
    id: "need-help",
    title: "Need Help?",
    body: `<p>If you have any questions about refunds or cancellations, our support team is here to help you.</p>`,
    icon: "headset",
  },
];

export const DEFAULT_REFUND_CMS_CONFIG = {
  schemaVersion: 2,
  heroBackgroundUrl: "",
  intro: REFUND_HERO_INTRO,
  sections: DEFAULT_REFUND_SECTIONS.map((s) => ({ ...s, visible: true })),
};

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function slugify(value, fallback) {
  const slug = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

function mergeSections(defaults, incoming) {
  const byId = new Map();
  if (Array.isArray(incoming)) {
    for (const item of incoming) {
      if (!isPlainObject(item)) continue;
      const id = slugify(item.id || item.title, "");
      if (id) byId.set(id, item);
    }
  }
  const allowedIcons = new Set(REFUND_SECTION_ICON_OPTIONS.map((o) => o.value));
  return defaults.map((base) => {
    const row = byId.get(base.id) || {};
    const iconValue = String(row.icon ?? base.icon ?? "file").trim();
    return {
      id: base.id,
      title: String(row.title ?? base.title ?? "").trim() || base.title,
      body: String(row.body ?? base.body ?? "").trim() || base.body,
      icon: allowedIcons.has(iconValue) ? iconValue : base.icon || "file",
      visible: row.visible !== false,
    };
  });
}

export function parseRefundCmsConfig(content) {
  if (!content || typeof content !== "string") {
    return {
      ...DEFAULT_REFUND_CMS_CONFIG,
      sections: DEFAULT_REFUND_CMS_CONFIG.sections.map((s) => ({ ...s })),
    };
  }

  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) {
    return {
      ...DEFAULT_REFUND_CMS_CONFIG,
      sections: DEFAULT_REFUND_CMS_CONFIG.sections.map((s) => ({ ...s })),
    };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!isPlainObject(parsed)) {
      return {
        ...DEFAULT_REFUND_CMS_CONFIG,
        sections: DEFAULT_REFUND_CMS_CONFIG.sections.map((s) => ({ ...s })),
      };
    }

    const defaults = DEFAULT_REFUND_CMS_CONFIG;
    return {
      schemaVersion: 2,
      heroBackgroundUrl: String(parsed.heroBackgroundUrl ?? defaults.heroBackgroundUrl).trim(),
      intro: defaults.intro,
      sections: mergeSections(defaults.sections, parsed.sections),
    };
  } catch {
    return {
      ...DEFAULT_REFUND_CMS_CONFIG,
      sections: DEFAULT_REFUND_CMS_CONFIG.sections.map((s) => ({ ...s })),
    };
  }
}

export function serializeRefundCmsConfig(config) {
  const defaults = DEFAULT_REFUND_CMS_CONFIG;
  const source = isPlainObject(config) ? config : defaults;
  return JSON.stringify(
    {
      schemaVersion: 2,
      heroBackgroundUrl: String(source.heroBackgroundUrl ?? defaults.heroBackgroundUrl).trim(),
      intro: defaults.intro,
      sections: mergeSections(defaults.sections, source.sections),
    },
    null,
    2,
  );
}

export function refundConfigFromCmsRecord(record) {
  return parseRefundCmsConfig(record?.content);
}

export function refundCmsPayloadFromConfig(config) {
  const content = serializeRefundCmsConfig(config);
  const data = parseRefundCmsConfig(content);
  return {
    title: "Refund & Cancellation Policy",
    description: (data.intro || "").slice(0, 200),
    content,
  };
}

export function buildRefundPageModel(page) {
  const config = parseRefundCmsConfig(page?.content);
  const sections = (config.sections?.length ? config.sections : DEFAULT_REFUND_SECTIONS).filter(
    (section) => section.visible !== false,
  );
  return {
    title: "Refund & Cancellation Policy",
    intro: config.intro || REFUND_HERO_INTRO,
    heroBackgroundUrl: config.heroBackgroundUrl || "",
    effectiveDate: page?.effectiveDate || null,
    updatedAt: page?.updatedAt || null,
    sections,
    fromCms: Boolean(page?.content && String(page.content).trim().startsWith("{")),
  };
}
